/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect, useState } from 'react';

import { groupBy, orderBy } from 'lodash';
import type { PlayerWithPlugins } from 'video.js';

import type { QuizQuestionPrompt } from 'bundles/compound-assessments/types/FormParts';
import type { VideoQuizModel } from 'bundles/item-lecture';
import { VideoQuiz } from 'bundles/video-quiz/index';
import type { InVideoQuestion } from 'bundles/video-quiz/types';

type PropsFromCaller = {
  player: PlayerWithPlugins;
  videoQuizModel: VideoQuizModel;
  courseSlug: string;
  itemId: string;
};

type IVQ = InVideoQuestion<QuizQuestionPrompt>;

const LearnerInVideoQuiz = ({ videoQuizModel, player, itemId, courseSlug }: PropsFromCaller) => {
  const [activeQuestion, setActiveQuestion] = useState<IVQ | null>(null);
  const [videoQuizQueue, setVideoQuizQueue] = useState<IVQ[]>([]);

  const resumePlayback = () => {
    player.emitter.trigger('videoQuizView.hidden');
    setActiveQuestion(null);

    const videoLength = player.duration();
    const currentTime = player.currentTime();

    if (typeof videoLength === 'number' && typeof currentTime === 'number' && videoLength > currentTime) {
      player.play();
    }
  };

  const goToNextIVQorResumePlayback = () => {
    if (videoQuizQueue.length > 0) {
      const currentActiveQuestion = videoQuizQueue.slice(-1)[0]; // same as calling .pop() but without mutating the array
      const quizQueueWithLastElementRemoved = videoQuizQueue.slice(0, -1);

      if (currentActiveQuestion) {
        setActiveQuestion(currentActiveQuestion);
        setVideoQuizQueue(quizQueueWithLastElementRemoved);
      }
    } else {
      resumePlayback();
    }
  };

  useEffect(() => {
    /**
     * Initializes cuepoints for in-video quizzes (IVQs)
     */
    const initializeVideoQuizListeners = () => {
      if (videoQuizModel) {
        const { questions = [] } = videoQuizModel;

        const cuepointMap = groupBy(orderBy(questions, 'video.order', 'desc'), 'video.cuePointMs');
        Object.keys(cuepointMap).forEach((cuePointMs) => {
          const questionsAtCuepoint = cuepointMap[cuePointMs];
          const cuePoint = parseInt(cuePointMs, 10) / 1000;

          player.addCuepoint({ at: cuePoint, visible: true }, () => {
            const videoQuestions = [...questionsAtCuepoint];
            const scrollContainer = document.querySelector('[data-id="item-scroll-container"]');

            player.pause();
            player.emitter.trigger('videoQuizView.visible');

            if (scrollContainer) {
              scrollContainer.scrollTop = 0;
            }

            const currentActiveQuestion = videoQuestions.pop();
            setActiveQuestion(currentActiveQuestion);
            setVideoQuizQueue(videoQuestions);
          });
        });
      }
    };

    player?.on('loadedmetadata', initializeVideoQuizListeners);

    return () => {
      player?.off('loadedmetadata', initializeVideoQuizListeners);
    };
  }, [player, videoQuizModel]);

  return (
    <VideoQuiz
      key={activeQuestion?.id}
      onContinue={goToNextIVQorResumePlayback}
      itemId={itemId}
      courseSlug={courseSlug}
      sessionId={videoQuizModel?.sessionId}
      question={activeQuestion}
    />
  );
};

export default LearnerInVideoQuiz;
